import { TranslationKey } from '@/modules/i18n/types'
import { ChallengeService } from '@duck/api-definitions'

export const getStatusBadgeColor = (status: ChallengeService.ChallengeStatusEnum): string => {
  const statusColors = {
    [ChallengeService.ChallengeStatusEnum.Unbooted]: 'bg-warning/10 text-warning border border-warning/20',
    [ChallengeService.ChallengeStatusEnum.Ongoing]: 'bg-warning/10 text-warning border border-warning/20',
    [ChallengeService.ChallengeStatusEnum.Finished]: 'bg-success/10 text-success border border-success/20',
    [ChallengeService.ChallengeStatusEnum.Aborted]: 'bg-error/10 text-error border border-error/20',
  }
  return statusColors[status] || 'bg-muted/10 text-muted border border-muted/20'
}

export const formatChallengeStatus = (status: ChallengeService.ChallengeStatusEnum): TranslationKey => {
  const statusMap: Record<ChallengeService.ChallengeStatusEnum, TranslationKey> = {
    [ChallengeService.ChallengeStatusEnum.Unbooted]: 'challenges.unbooted',
    [ChallengeService.ChallengeStatusEnum.Ongoing]: 'challenges.ongoing',
    [ChallengeService.ChallengeStatusEnum.Finished]: 'challenges.finished',
    [ChallengeService.ChallengeStatusEnum.Aborted]: 'challenges.aborted',
  }
  return statusMap[status]
}

export const getFeedbackStatusBadgeColor = (feedbackStatus?: ChallengeService.FeedbackStatusEnum): string => {
  if (!feedbackStatus) {
    return 'bg-muted/10 text-muted border border-muted/20'
  }

  const feedbackStatusColors: Partial<Record<ChallengeService.FeedbackStatusEnum, string>> = {
    'unprocessed': 'bg-muted/10 text-muted border border-muted/20',
    'processed': 'bg-success/10 text-success border border-success/20',
  }
  return feedbackStatusColors[feedbackStatus] || 'bg-warning/10 text-warning border border-warning/20'
}


const feedbackStatusMap: Record<ChallengeService.FeedbackStatusEnum, TranslationKey> = {
  'unprocessed': 'challenges.feedbackUnprocessed',
  'processing1': 'challenges.feedbackProcessing',
  'processing2': 'challenges.feedbackProcessing',
  'processing3': 'challenges.feedbackProcessing',
  'processing4': 'challenges.feedbackProcessing',
  'processing5': 'challenges.feedbackProcessing',
  'processing6': 'challenges.feedbackProcessing',
  'processing7': 'challenges.feedbackProcessing',
  'processing8': 'challenges.feedbackProcessing',
  'processing9': 'challenges.feedbackProcessing',
  'processing10': 'challenges.feedbackProcessing',
  'processing11': 'challenges.feedbackProcessing',
  'processing12': 'challenges.feedbackProcessing',
  'processing13': 'challenges.feedbackProcessing',
  'processing14': 'challenges.feedbackProcessing',
  'processing15': 'challenges.feedbackProcessing',
  'processed': 'challenges.feedbackProcessed',
}

export const formatFeedbackStatus = (feedbackStatus?: ChallengeService.FeedbackStatusEnum): TranslationKey => {
  if (!feedbackStatus) {
    return 'challenges.unsubmitted'
  }
  return feedbackStatusMap[feedbackStatus] || 'common.unknown'
}

export const formatFeedbackGrade = (grade?: number): string => {
  if (grade === undefined || grade === null) return 'N/A'
  return `${grade} / 10`
}

export type ChallengeColumnFilterType = 'text' | 'enum' | 'async-search'

/** Column keys are challenge fields, plus synthetic keys derived from nested data (e.g. weaponOfChoice ← workState.chosenWeapon). */
export type ChallengeColumnKey = keyof ChallengeService.Challenge | 'weaponOfChoice'

export interface Column {
  key: ChallengeColumnKey
  label: TranslationKey
  sortable: boolean
  visible: boolean
  /** When set, a filter control is shown in the column header. filterKey is the key used in table settings. */
  filterType?: ChallengeColumnFilterType
  filterKey?: string
}

/** Status order for filter dropdown; labels match row display via formatStatus. */
const statusFilterOrder: ChallengeService.ChallengeStatusEnum[] = [
  ChallengeService.ChallengeStatusEnum.Unbooted,
  ChallengeService.ChallengeStatusEnum.Ongoing,
  ChallengeService.ChallengeStatusEnum.Finished,
  ChallengeService.ChallengeStatusEnum.Aborted,
]

/** Options for the status filter (backend queryFilters.status). Uses same labels as table rows. */
export const statusFilterOptions: { value: string; label: TranslationKey }[] = statusFilterOrder.map(
  (value) => ({ value, label: formatChallengeStatus(value) })
)

export type FeedbackStatusFilterOption = ChallengeService.FeedbackStatusFilterOption

const feedbackStatusFilterOrder: FeedbackStatusFilterOption[] = [
  ChallengeService.FeedbackStatusFilterOption.NoFeedback,
  ChallengeService.FeedbackStatusFilterOption.Unprocessed,
  ChallengeService.FeedbackStatusFilterOption.Processing,
  ChallengeService.FeedbackStatusFilterOption.Processed,
]

const feedbackStatusFilterLabelMap: Record<FeedbackStatusFilterOption, TranslationKey> = {
  [ChallengeService.FeedbackStatusFilterOption.NoFeedback]: 'challenges.unsubmitted',
  [ChallengeService.FeedbackStatusFilterOption.Unprocessed]: 'challenges.feedbackUnprocessed',
  [ChallengeService.FeedbackStatusFilterOption.Processing]: 'challenges.feedbackProcessing',
  [ChallengeService.FeedbackStatusFilterOption.Processed]: 'challenges.feedbackProcessed',
}

/** Options for the feedback status filter (backend queryFilters.feedbackStatus). */
export const feedbackStatusFilterOptions: { value: FeedbackStatusFilterOption; label: TranslationKey }[] =
  feedbackStatusFilterOrder.map((value) => ({ value, label: feedbackStatusFilterLabelMap[value] }))

/** Badge color for feedback status filter dropdown options (matches row badge colors). */
export const getFeedbackStatusFilterBadgeColor = (option: FeedbackStatusFilterOption): string => {
  const colors: Record<FeedbackStatusFilterOption, string> = {
    [ChallengeService.FeedbackStatusFilterOption.NoFeedback]: 'bg-muted/10 text-muted border border-muted/20',
    [ChallengeService.FeedbackStatusFilterOption.Unprocessed]: 'bg-muted/10 text-muted border border-muted/20',
    [ChallengeService.FeedbackStatusFilterOption.Processing]: 'bg-warning/10 text-warning border border-warning/20',
    [ChallengeService.FeedbackStatusFilterOption.Processed]: 'bg-success/10 text-success border border-success/20',
  }
  return colors[option]
}

/** Order for the weapon-of-choice filter dropdown; values map to backend queryFilters.weaponOfChoice. */
const weaponOfChoiceFilterOrder: ChallengeService.TaskWeaponsOfChoiceEnum[] = [
  ChallengeService.TaskWeaponsOfChoiceEnum.Sql,
  ChallengeService.TaskWeaponsOfChoiceEnum.Python,
]

const weaponOfChoiceLabelMap: Record<ChallengeService.TaskWeaponsOfChoiceEnum, TranslationKey> = {
  [ChallengeService.TaskWeaponsOfChoiceEnum.Sql]: 'challenges.weaponSql',
  [ChallengeService.TaskWeaponsOfChoiceEnum.Python]: 'challenges.weaponPython',
}

export const formatWeaponOfChoice = (weapon: ChallengeService.TaskWeaponsOfChoiceEnum): TranslationKey =>
  weaponOfChoiceLabelMap[weapon]

/** Options for the weapon-of-choice filter (backend queryFilters.weaponOfChoice). */
export const weaponOfChoiceFilterOptions: { value: string; label: TranslationKey }[] =
  weaponOfChoiceFilterOrder.map((value) => ({ value, label: weaponOfChoiceLabelMap[value] }))

/** Badge color for the weapon-of-choice column/filter options. */
export const getWeaponOfChoiceBadgeColor = (weapon: ChallengeService.TaskWeaponsOfChoiceEnum): string => {
  const colors: Record<ChallengeService.TaskWeaponsOfChoiceEnum, string> = {
    [ChallengeService.TaskWeaponsOfChoiceEnum.Sql]: 'bg-accent/10 text-accent border border-accent/20',
    [ChallengeService.TaskWeaponsOfChoiceEnum.Python]: 'bg-success/10 text-success border border-success/20',
  }
  return colors[weapon] || 'bg-muted/10 text-muted border border-muted/20'
}

export const baseColumns: Column[] = [
  { key: 'id', label: 'common.id', sortable: true, visible: false },
  { key: 'slug', label: 'tasks.slug', sortable: true, visible: false, filterType: 'text', filterKey: 'slug' },
  { key: 'userId', label: 'common.user', sortable: true, visible: true, filterType: 'async-search', filterKey: 'userId' },
  { key: 'taskId', label: 'tasks.task', sortable: true, visible: true, filterType: 'async-search', filterKey: 'taskId' },
  { key: 'status', label: 'challenges.status', sortable: true, visible: true, filterType: 'enum', filterKey: 'status' },
  { key: 'feedbackStatus', label: 'challenges.feedbackStatus', sortable: true, visible: true, filterType: 'enum', filterKey: 'feedbackStatus' },
  { key: 'workTime', label: 'common.workTime', sortable: true, visible: true },
  { key: 'feedbackGrade', label: 'challenges.feedbackGrade', sortable: true, visible: true },
  { key: 'weaponOfChoice', label: 'challenges.weaponOfChoice', sortable: false, visible: true, filterType: 'enum', filterKey: 'weaponOfChoice' },
  { key: 'startTime', label: 'challenges.startTime', sortable: true, visible: true },
  { key: 'endTime', label: 'challenges.endTime', sortable: true, visible: true },
  { key: 'lastWorkTimeUpdate', label: 'challenges.lastWorkTimeUpdate', sortable: true, visible: false },
]
